import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import Button from "@material-ui/core/Button";
import sortBy from "lodash/sortBy";
import Dialog from "@material-ui/core/Dialog";
import { Flex, PromptHeader, PromptBody, PromptButton, PrimaryHeader } from "../styles/global";
import { getSymptoms, deleteSymptom } from "../utils/api.utils";
import { SymptomEditor } from "../pages/SymptomEditor";
import { Symptom } from "../models";
import { useIsComponentMounted } from '../utils/hooks.utils'
import defaultSymptom from "../data/defaultSymptom";
import symptomIcon from "../assets/symptom.svg";

export const SymptomList = () => {
  const [showEditScreen, setEditScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSymptom, setCurrentSymptom] = useState<any>();
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isComponentMounted = useIsComponentMounted();

  const fetchSymptoms = useCallback(() => {
    getSymptoms()
      .then((result: any) => {
        if (isComponentMounted.current) {
          setLoading(false)
          const payload = result as any;
          setSymptoms(sortBy(payload, "created_at"));
        }
      })
      .catch((err) => {
        if (isComponentMounted.current) {
          setSymptoms([])
          setLoading(false)
        }
      });
  }, [isComponentMounted]);

  const createNewSymptom = () => {
    setCurrentSymptom(defaultSymptom);
    setEditScreen(true);
  };

  const selectSymptom = (symptom: any) => {
    setCurrentSymptom(symptom);
    setEditScreen(true);
  };

  const handleSymptomDelete = (symptom: any) => {
    deleteSymptom(symptom.id)
      .then((res) => console.log(res))
      .catch((err) => console.error(err));
    setCurrentSymptom(undefined);
    setShowDeleteModal(false);
    setTimeout(() => fetchSymptoms(), 1000);
    fetchSymptoms();
  };

  const displayDeleteModal = (symptom: any) => {
    if (!symptom) {
      return;
    }
    setCurrentSymptom(symptom);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    fetchSymptoms()
  }, [fetchSymptoms]);

  if (showEditScreen) {
    window.scrollTo(0, 0);
    return (
      <SymptomEditor
        shouldDisplay={setEditScreen}
        fetchSymptoms={fetchSymptoms}
        symptom={currentSymptom}
      />
    );
  }
  return (
    <div style={{ padding: '0 0 24px 0', width: '100%', boxSizing: 'border-box' }}>
      <Flex justify="space-between" alignItems="center" style={{ marginBottom: '32px' }}>
        <PrimaryHeader style={{ margin: 0 }}>Symptoms</PrimaryHeader>
        <Button
          variant="contained"
          color="primary"
          style={{ borderRadius: '8px', padding: '10px 24px', fontWeight: 600, boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)' }}
          onClick={() => createNewSymptom()}>
          + New Symptom
        </Button>
      </Flex>

      <div id="symptoms-view">
        {!loading ?
          symptoms && symptoms.length >= 1 ?
            symptoms.map((item: any) => (
              <SymptomCard
                key={item.id + item.name}
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <SymptomInfo direction="row" alignItems="center" style={{ flex: 1 }}>
                  <IconContainer>
                    <img src={symptomIcon} alt="Symptom" />
                  </IconContainer>
                  <SymptomTextContent direction="column">
                    <Flex alignItems="center" style={{ marginBottom: '4px' }}>
                      <SymptomTitle>{item.name || "Untitled Symptom"}</SymptomTitle>
                    </Flex>
                    <Flex wrap="wrap" alignItems="center">
                      <StatPill>ID: {item.id}</StatPill>
                    </Flex>
                  </SymptomTextContent>
                </SymptomInfo>

                <ActionGroup alignItems="center">
                  <ActionButton
                    variant="outlined"
                    onClick={() => selectSymptom(item)}
                  >
                    Edit
                  </ActionButton>
                  <DeleteButton onClick={(e) => {
                    (e.currentTarget as HTMLElement).blur();
                    displayDeleteModal(item);
                  }}>
                    Delete
                  </DeleteButton>
                </ActionGroup>
              </SymptomCard>
            ))
            :
            <NotFound>No Symptoms Found</NotFound>
          :
          <NotFound>Loading...</NotFound>
        }

        <Dialog
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          PaperProps={{
            style: { borderRadius: '16px', padding: '8px', maxWidth: '450px' }
          }}
        >
          <ModalContent direction="column" alignItems="center">
            <div className="icon-container delete">!</div>
            <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
              Delete Symptom?
            </PromptHeader>
            <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
              Are you sure you want to delete this symptom? All data associated with it will be permanently removed.
            </PromptBody>
            <Flex style={{ marginTop: '32px', width: '100%', gap: '12px' }} justify="center">
              <PromptButton
                autoFocus
                onClick={() => setShowDeleteModal(false)}
                variant="outlined"
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  borderColor: '#e2e8f0'
                }}
              >
                Cancel
              </PromptButton>
              <PromptButton
                onClick={() => handleSymptomDelete(currentSymptom)}
                variant="contained"
                style={{
                  background: '#c10000',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  boxShadow: '0 4px 12px rgba(193, 0, 0, 0.2)'
                }}
              >
                Delete
              </PromptButton>
            </Flex>
          </ModalContent>
        </Dialog>
      </div>
    </div>
  );
};

const ModalContent = styled(Flex)`
  padding: 32px;
  
  .icon-container {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 20px;
    
    &.delete {
      background: #fff5f5;
      color: #e53e3e;
      border: 2px solid #feb2b2;
    }
  }
`;

const SymptomCard = styled(Flex)`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.15);
    border-color: #cbd5e0;
  }
`;

const IconContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #fff5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  flex-shrink: 0;

  img {
    width: 28px;
    height: 28px;
    opacity: 0.8;
  }
`;

const SymptomInfo = styled(Flex)``;

const SymptomTextContent = styled(Flex)`
  flex: 1;
`;

const SymptomTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  margin-right: 12px;
`;

const StatPill = styled.span`
  font-size: 13px;
  color: #718096;
  background: #f7fafc;
  padding: 2px 10px;
  border-radius: 20px;
  margin-right: 12px;
  border: 1px solid #edf2f7;
`;

const ActionGroup = styled(Flex)`
  margin-left: 24px;
  gap: 12px;
`;

const ActionButton = styled(Button)`
  && {
    min-width: 100px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    border-radius: 8px;
    border-color: #e2e8f0;
    color: #4a5568;
    &:hover {
      background: #f7fafc;
      border-color: #cbd5e0;
    }
  }
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 80px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    color: #e53e3e;
    &:hover {
      background: #fff5f5;
    }
  }
`;

const NotFound = styled.h4`
  font-weight: 600;
  margin-top: 80px;
  font-size: 20px;
  color: #718096;
  text-align: center;
`;